import { Input } from 'antd';
import classNames from 'classnames';
import React from 'react';
import ErrorMessage from '../../errorMessage';
import InputTitle from '../../inputTitle';
import type { IProps } from './types';
import style from './style.module.scss';

/**
 * Multi-line text field with a title and inline error message. Renders an Ant Design Input.TextArea using the size, rows, autoSize and maxLength from props, reports edits as a name and value pair through the local change handler, and appends an ErrorMessage when errorMessage is set.
 *
 * @param props - Field name and id, value, title, placeholder, sizing options, disabled flag and error message.
 * @returns The rendered textarea wrapper element.
 */
const CustomTextarea = (props: IProps): React.JSX.Element => {
  const { size = 'large', name = '', id = '' } = props;

  /**
   * Relays textarea edits back to the owner of the field. Invokes props.onChange with the field name and the event target's value when a handler was supplied.
   *
   * @param e - The textarea change event.
   * @returns Nothing.
   */
  const onChange = (e: any) => {
    if (props.onChange) {
      props.onChange(name, e.target.value);
    }
  };
  return (
    <div className={classNames(style.customInputWrapper, props.className)}>
      <InputTitle title={props.title} isRequired={props.isRequired} />

      <Input.TextArea
        id={id}
        name={props.name}
        aria-label={props['aria-label'] ?? props.title}
        size={size}
        className={classNames(
          style.customInput,
          props.inputClassName,
          props.isError ? style['customButton--error'] : '',
        )}
        placeholder={props.placeholder}
        onChange={onChange}
        value={props.value}
        disabled={props.disabled}
        rows={props.rows}
        autoSize={props.autoSize}
        maxLength={props.maxLength}
      />
      {props.errorMessage && (
        <ErrorMessage className={style.errorMessage} errorMessage={props.errorMessage} hasIcon />
      )}
    </div>
  );
};

export default CustomTextarea;
